import { type IPublicTransportConstants } from '../constants/public-transport.constants';
import { TicketType } from '../models/ticket-type.enum';
import { ZoneType } from '../models/zone-type.enum';

export interface IPublicTransportParameters {
  preferredTicketType: TicketType;
  zonesToTravelWithin: ZoneType[];
  numberOfTravelDaysPerMonth: number;
}

export interface IPublicTransportResult {
  costInSek: number;
}

export class PublicTransportCalculator {
  public static calculateCost(params: IPublicTransportParameters, constants: IPublicTransportConstants): IPublicTransportResult {
    let numberOfOneZoneTripsPerMonth = 0;
    let numberOfTwoZoneTripsPerMonth = 0;
    let numberOfthreeZoneTripsPerMonth = 0;

    if (params.zonesToTravelWithin.length === 1) {
      numberOfOneZoneTripsPerMonth = params.numberOfTravelDaysPerMonth;
    } else if (params.zonesToTravelWithin.length === 2) {
      numberOfTwoZoneTripsPerMonth = params.numberOfTravelDaysPerMonth;
    } else if (params.zonesToTravelWithin.length === 3) {
      numberOfthreeZoneTripsPerMonth = params.numberOfTravelDaysPerMonth;
    }

    let periodTicketOneZonePriceInSek = 0;
    let periodTicketTwoZonesPriceInSek = 0;
    let periodTicketThreeZonesPriceInSek = 0;
    if (params.preferredTicketType === TicketType.ThirtyDays) {
      periodTicketOneZonePriceInSek = constants.periodTicket30DaysOneZonePriceInSek;
      periodTicketTwoZonesPriceInSek = constants.periodTicket30DaysTwoZonesPriceInSek;
      periodTicketThreeZonesPriceInSek = constants.periodTicket30DaysThreeZonesPriceInSek;
    } else if (params.preferredTicketType === TicketType.NinetyDays) {
      periodTicketOneZonePriceInSek = constants.periodTicket90DaysOneZonePriceInSek / 3;
      periodTicketTwoZonesPriceInSek = constants.periodTicket90DaysTwoZonesPriceInSek / 3;
      periodTicketThreeZonesPriceInSek = constants.periodTicket90DaysThreeZonesPriceInSek / 3;
    } else if (params.preferredTicketType === TicketType.ThreeHundredSixtyFiveDays) {
      periodTicketOneZonePriceInSek = constants.periodTicket365DaysOneZonePriceInSek / 12;
      periodTicketTwoZonesPriceInSek = constants.periodTicket365DaysTwoZonesPriceInSek / 12;
      periodTicketThreeZonesPriceInSek = constants.periodTicket365DaysThreeZonesPriceInSek / 12;
    }

    const costOnlySingleTickets =
      numberOfOneZoneTripsPerMonth * constants.singleTicketOneZonePriceInSek +
      numberOfTwoZoneTripsPerMonth * constants.singleTicketTwoZonesPriceInSek +
      numberOfthreeZoneTripsPerMonth * constants.singleTicketThreeZonesPriceInSek;

    const costOnlyDayTickets =
      numberOfOneZoneTripsPerMonth * constants.dayTicketOneZonePriceInSek +
      numberOfTwoZoneTripsPerMonth * constants.dayTicketTwoZonesPriceInSek +
      numberOfthreeZoneTripsPerMonth * constants.dayTicketThreeZonesPriceInSek;

    const costOnlyThreeDayTickets =
      Math.ceil(numberOfOneZoneTripsPerMonth / 3) * constants.threeDaysTicketOneZonePriceInSek +
      Math.ceil(numberOfTwoZoneTripsPerMonth / 3) * constants.threeDaysTicketTwoZonesPriceInSek +
      Math.ceil(numberOfthreeZoneTripsPerMonth / 3) * constants.threeDaysTicketThreeZonesPriceInSek;

    let costInSek = 0;

    switch (params.preferredTicketType as TicketType) {
      case TicketType.SingleTicket:
        costInSek = 2 * costOnlySingleTickets;
        break;
      case TicketType.DayTicket:
        costInSek = costOnlyDayTickets;
        break;
      case TicketType.ThreeDayTicket:
        costInSek = costOnlyThreeDayTickets;
        break;
      case TicketType.ThirtyDays:
      case TicketType.NinetyDays:
      case TicketType.ThreeHundredSixtyFiveDays:
        if (params.zonesToTravelWithin.length === 1) costInSek = Math.round(periodTicketOneZonePriceInSek);
        else if (params.zonesToTravelWithin.length === 2) costInSek = Math.round(periodTicketTwoZonesPriceInSek);
        else if (params.zonesToTravelWithin.length === 3) costInSek = Math.round(periodTicketThreeZonesPriceInSek);
        break;
      default:
        throw new Error('Unknown preferred season ticket type');
    }

    return {
      costInSek,
    };
  }
}

